import { useEffect, useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import Dot from 'components/@extended/Dot';
import { useMode } from 'contexts/ModeContext';
import { MODE_FORCED_EVENT } from 'utils/mode';

const NOT_VERIFIED_MESSAGE =
  'A sua conta ainda não está validada. Para ver e operar em Produção, valide a sua conta. Até lá, está em modo Sandbox.';

const OPTIONS = {
  live: { label: 'Produção', color: 'success' },
  test: { label: 'Sandbox', color: 'warning' }
};

// ==============================|| HEADER CONTENT - SELETOR DE MODO ||============================== //

export default function ModeSelector() {
  const { mode, canUseLive, setMode } = useMode();
  const [notice, setNotice] = useState(null);

  // O backend recusou Produção (403): informar que a conta tem de ser validada
  useEffect(() => {
    const onForced = (event) => setNotice(event.detail?.message || NOT_VERIFIED_MESSAGE);
    window.addEventListener(MODE_FORCED_EVENT, onForced);
    return () => window.removeEventListener(MODE_FORCED_EVENT, onForced);
  }, []);

  const handleChange = (event) => {
    const next = event.target.value;
    if (!setMode(next)) {
      setNotice(NOT_VERIFIED_MESSAGE);
    }
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      <FormControl size="small">
        <Select
          id="header-mode-select"
          value={mode}
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Modo de dados: Produção ou Sandbox' }}
          renderValue={(value) => (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Dot color={OPTIONS[value].color} size={8} />
              <Typography variant="subtitle2">{OPTIONS[value].label}</Typography>
            </Stack>
          )}
          sx={(theme) => ({
            minWidth: { xs: 120, sm: 140 },
            ...(mode === 'test' && {
              bgcolor: 'warning.lighter',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.warning.main }
            })
          })}
        >
          <MenuItem value="live" disabled={!canUseLive} sx={{ alignItems: 'flex-start' }}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <Dot color="success" size={8} />
              <Box>
                <Typography variant="subtitle2">Produção</Typography>
                <Typography variant="caption" color="text.secondary">
                  {canUseLive ? 'Pagamentos reais' : 'Requer conta validada'}
                </Typography>
              </Box>
            </Stack>
          </MenuItem>
          <MenuItem value="test" sx={{ alignItems: 'flex-start' }}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <Dot color="warning" size={8} />
              <Box>
                <Typography variant="subtitle2">Sandbox</Typography>
                <Typography variant="caption" color="text.secondary">
                  Dados de teste
                </Typography>
              </Box>
            </Stack>
          </MenuItem>
        </Select>
      </FormControl>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
